/**
 * Redemptions Page
 * Shows redemption history with role-based filtering
 * - Partners: See only redemptions from their location(s)
 * - Admin/Manager/Finance/Support: See all redemptions
 *
 * Features:
 * - Server-side pagination for scalability (100k+ vouchers)
 * - Server-side search (debounced)
 * - Server-side filtering (status, date, org, type)
 * - Real-time updates via Supabase subscriptions
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  usePaginatedRedemptionsWithAnalytics,
  useDailyRedemptionData,
  useMarkVoucherVendorPaid,
} from "../hooks/useRedemptions";
import type {
  PaginatedRedemptionsParams,
  RedemptionDateBasis,
} from "../services/redemptions.service";
import {
  exportRedemptionsToCSV,
  REDEMPTION_DATE_BASIS_LABELS,
  getRedemptionsPaginated,
} from "../services/redemptions.service";
import {
  getUserLocations,
  getPartnerOrgName,
} from "../services/locations.service";
import type { Location } from "../types/organization.types";
import { useNavigate, useSearchParams } from "react-router-dom";
import LineChart from "../components/charts/LineChart";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import { StatCardSkeleton } from "../components/ui/Skeleton";
import { useConfirmDialog } from "../components/ui/ConfirmDialog";
import {
  XCircleIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  GiftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import IssueRewardModal from "../components/vouchers/IssueRewardModal";
import RedemptionsReportSearchField from "../components/redemption/RedemptionsReportSearchField";
import { isFeatureEnabled } from "../utils/featureFlags";

// Debounce hook for search
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function Redemptions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Filter states - these will be passed to server-side query
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Which timestamp the date window means. "issued" reproduces the historical behaviour
  // (vouchers.created_at) and stays the default; "redeemed" makes vouchers redeemed on a
  // chosen day discoverable, which was impossible before.
  const [dateBasis, setDateBasis] = useState<RedemptionDateBasis>("issued");
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "redeemed" | "unredeemed" | "expired" | "test" | "cancelled"
  >("all");
  const [voucherTypeFilter, setVoucherTypeFilter] = useState("");
  const [eventIdFilter, setEventIdFilter] = useState("");

  // Sorting state
  const [sortField, setSortField] = useState<
    "date" | "value" | "organization" | "status"
  >("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Pagination state - page is managed separately since it resets on filter changes
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50; // Server-side page size

  // Debounce search input to avoid excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Build params object for server-side query
  const queryParams: PaginatedRedemptionsParams = useMemo(
    () => ({
      page: currentPage,
      pageSize,
      search: debouncedSearchTerm || undefined,
      status: statusFilter,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      dateBasis,
      organizationId: selectedOrganization || undefined,
      locationName: selectedLocation || undefined,
      eventId: eventIdFilter || undefined,
      voucherType: voucherTypeFilter || undefined,
      sortField,
      sortDirection,
    }),
    [
      currentPage,
      pageSize,
      debouncedSearchTerm,
      statusFilter,
      dateFrom,
      dateTo,
      dateBasis,
      selectedOrganization,
      selectedLocation,
      eventIdFilter,
      voucherTypeFilter,
      sortField,
      sortDirection,
    ],
  );

  // Use paginated React Query hook with real-time updates
  const {
    redemptions,
    totalCount,
    totalPages,
    nameLookupFailures,
    analytics,
    filterOptions,
    isLoading: loading,
    isFetching,
    error,
    cancelVoucher,
    testVoucher,
    refetch,
  } = usePaginatedRedemptionsWithAnalytics(queryParams, {
    enableRealtime: false, // Temporarily disabled - realtime subscription causing blocking issues
  });

  // Fetch chart data separately (last 30 days)
  const { data: chartData = [] } = useDailyRedemptionData(30, {
    enableRealtime: false, // Temporarily disabled - realtime subscription causing blocking issues
  });

  // Local UI state
  const [_partnerLocations, setPartnerLocations] = useState<Location[]>([]);
  const [partnerDisplayName, setPartnerDisplayName] = useState<string | null>(
    null,
  );
  const [vendorPaidPending, setVendorPaidPending] = useState<string | null>(
    null,
  );
  const markVendorPaidMutation = useMarkVoucherVendorPaid();
  // Managers see Vendor Paid but cannot change it (owner 2026-10-01; DB-enforced too).
  // Managers see Vendor Paid but cannot change it (2026-10-01-001); Regional / Territory Managers have Redemptions
  // view-only (mig 1241: mark_voucher_vendor_paid refuses them).
  const canToggleVendorPaid = ![
    "manager",
    "regional_manager",
    "territory_manager",
  ].includes(user?.role ?? "");
  const [cancellingVoucherId, setCancellingVoucherId] = useState<string | null>(
    null,
  );
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
  const [showIssueRewardModal, setShowIssueRewardModal] = useState(false);
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  // Reset to page 1 when filters change (not when page itself changes)
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearchTerm,
    statusFilter,
    dateFrom,
    dateTo,
    dateBasis,
    selectedOrganization,
    selectedLocation,
    eventIdFilter,
    voucherTypeFilter,
    sortField,
    sortDirection,
  ]);

  // Copy email to clipboard
  const handleCopyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(null), 2000);
    } catch (err) {
      console.error("Failed to copy email:", err);
    }
  };

  // Initialize eventId filter from URL params
  useEffect(() => {
    const eventId = searchParams.get("eventId");
    if (eventId) {
      setEventIdFilter(eventId);
    }
  }, [searchParams]);

  // Helper function to get responsive font size based on value length
  // Prevents numbers from overflowing card containers when screen resizes
  // More aggressive scaling to prevent wrapping - reduces by ~50% for longer values
  const getResponsiveFontSize = (value: string): string => {
    const length = value.length;
    // Short values (1-4 chars): Large font
    if (length <= 4) {
      return "text-3xl sm:text-3xl md:text-2xl lg:text-3xl";
    }
    // Medium values (5-7 chars): Reduced font
    if (length <= 7) {
      return "text-2xl sm:text-2xl md:text-xl lg:text-2xl";
    }
    // Longer values (8-10 chars like $3080.00): 50% smaller
    if (length <= 10) {
      return "text-lg sm:text-lg md:text-base lg:text-lg";
    }
    // Very long values (11+ chars): Even smaller
    if (length <= 13) {
      return "text-base sm:text-base md:text-sm lg:text-base";
    }
    // Extremely long values: Smallest
    return "text-sm sm:text-sm md:text-xs lg:text-sm";
  };

  // Refs for synchronized horizontal scrolling
  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);

  // Sync scroll between top and bottom scroll bars
  const handleTopScroll = () => {
    if (topScrollRef.current && bottomScrollRef.current) {
      bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    if (topScrollRef.current && bottomScrollRef.current) {
      topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
    }
  };

  // Load partner locations (separate from React Query data)
  useEffect(() => {
    const loadPartnerLocations = async () => {
      if (!user || user.role !== "partner") {
        return;
      }

      try {
        const locations = await getUserLocations(user.id);
        console.log("[Redemptions] Partner locations loaded:", locations);
        setPartnerLocations(locations);

        // Compute display name
        let displayName = null;
        if (locations.length === 1) {
          displayName = locations[0].name;
          console.log(
            "[Redemptions] Single location, displaying:",
            displayName,
          );
        } else if (locations.length > 1) {
          const orgs = Array.from(
            new Set(
              locations.map((loc) => loc.organization?.name).filter(Boolean),
            ),
          );
          displayName = orgs.join(", ");
          console.log(
            "[Redemptions] Multiple locations, displaying orgs:",
            displayName,
          );
        } else {
          // No locations — promoter org or org with no location rows.
          // Fall back to the organization name.
          displayName = await getPartnerOrgName(user.id);
          console.log("[Redemptions] No locations, org fallback:", displayName);
        }
        setPartnerDisplayName(displayName);
      } catch (err) {
        console.error("[Redemptions] Error fetching partner locations:", err);
        setPartnerLocations([]);
        setPartnerDisplayName(null);
      }
    };

    loadPartnerLocations();
  }, [user]);

  // Reset location filter when organization changes
  useEffect(() => {
    setSelectedLocation("");
  }, [selectedOrganization]);

  // Get filter options from server. The org filter now selects by identity (UUID) so promoter
  // orgs (which may carry no facility) are selectable and duplicate names are distinguishable.
  const organizationOptions: { id: string; name: string }[] =
    filterOptions?.organizationOptions || [];

  // Available locations for the selected organization, looked up by org UUID.
  const availableLocations = useMemo(() => {
    if (!selectedOrganization || !filterOptions?.organizationsWithLocations) {
      return [];
    }
    const match = filterOptions.organizationsWithLocations.find(
      (o: { id: string; organization: string; locations: string[] }) =>
        o.id === selectedOrganization,
    );
    return match?.locations ?? [];
  }, [selectedOrganization, filterOptions?.organizationsWithLocations]);

  // Calculate pagination display values
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCount);

  const [isExporting, setIsExporting] = useState(false);
  // Paged export progress: rows fetched so far / expected total (null when idle).
  const [exportProgress, setExportProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  // Export failures were previously console.error only, so the spinner just stopped and the
  // operator was left staring at a page that looked like nothing had happened. The export now
  // has a real refusal path (the row ceiling in redemptions.service.ts), and a refusal nobody
  // can see is indistinguishable from a silent truncation -- the exact outcome the audit said
  // to avoid. Surfaced here instead.
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = async () => {
    if (totalCount === 0) {
      return;
    }
    setIsExporting(true);
    setExportError(null);
    setExportProgress(null);
    try {
      const result = await getRedemptionsPaginated(user?.id, user?.role, {
        ...queryParams,
        exportAll: true,
        onExportProgress: (done, total) => setExportProgress({ done, total }),
      });
      exportRedemptionsToCSV(result.data);
    } catch (err) {
      console.error("Export failed:", err);
      setExportError(
        err instanceof Error && err.message
          ? err.message
          : "The export could not be completed. Please try again, or narrow the filters if the result set is large.",
      );
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  const handleCancelVoucher = async (
    voucherId: string,
    voucherCode: string,
  ) => {
    const confirmed = await confirm({
      title: "Cancel Voucher",
      message: `Are you sure you want to cancel voucher ${voucherCode}? This action cannot be undone.`,
      confirmText: "Cancel Voucher",
      cancelText: "Keep Voucher",
    });

    if (!confirmed) {
      return;
    }

    try {
      setCancellingVoucherId(voucherId);
      await cancelVoucher(voucherId);
      // React Query will automatically refetch due to cache invalidation
    } catch (error) {
      console.error("Error cancelling voucher:", error);
      alert("Failed to cancel voucher. Please try again.");
    } finally {
      setCancellingVoucherId(null);
    }
  };

  const handleTestVoucher = async (voucherId: string, voucherCode: string) => {
    const confirmed = await confirm({
      title: "Mark Voucher as Test",
      message: `Are you sure you want to mark voucher ${voucherCode} as a test voucher? Test vouchers are excluded from reporting and analytics.`,
      confirmText: "Mark as Test",
      cancelText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    try {
      setCancellingVoucherId(voucherId);
      await testVoucher(voucherId);
      // React Query will automatically refetch due to cache invalidation
    } catch (error) {
      console.error("Error marking voucher as test:", error);
      alert("Failed to mark voucher as test. Please try again.");
    } finally {
      setCancellingVoucherId(null);
    }
  };

  const handleToggleVendorPaid = async (
    voucherId: string,
    nextPaid: boolean,
  ) => {
    // Unmark direction clears vendor_paid_at + vendor_paid_by — confirm.
    // Mark direction is immediate.
    if (!nextPaid) {
      const confirmed = await confirm({
        title: "Unmark vendor paid?",
        message:
          "This clears who marked it paid and when. The voucher itself is unaffected; only the reimbursement-tracking fields are cleared.",
        confirmText: "Unmark",
        cancelText: "Keep paid",
      });
      if (!confirmed) {
        return;
      }
    }

    try {
      setVendorPaidPending(voucherId);
      await markVendorPaidMutation.mutateAsync({
        voucherId,
        vendorPaid: nextPaid,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      alert(`Failed to update vendor paid status: ${message}`);
    } finally {
      setVendorPaidPending(null);
    }
  };

  // Handle auth/session errors - redirect to login if session is stale
  if (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    const isAuthError =
      errorMessage.includes("JWT") ||
      errorMessage.includes("token") ||
      errorMessage.includes("auth") ||
      errorMessage.includes("401") ||
      errorMessage.includes("403");

    if (isAuthError) {
      // Store current URL so user can return after re-login
      sessionStorage.setItem(
        "returnTo",
        window.location.pathname + window.location.search,
      );
      // Session is stale - redirect to login
      window.location.href = "/login?expired=true&reason=Session+expired";
      return null;
    }

    // Show error state for other errors
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Voucher History
            </h1>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 mb-4">
            Failed to load redemption data: {errorMessage}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
            <div className="h-6 w-48 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="h-10 w-32 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
        <div className="h-64 bg-gray-200 rounded-lg animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Voucher History</h1>
          {partnerDisplayName && (
            <p className="mt-1 text-lg text-gray-600">{partnerDisplayName}</p>
          )}
          {/* Real-time indicator - currently disabled */}
          {/* <div className="flex items-center gap-2 mt-2">
            <span className="flex items-center gap-1.5 text-xs text-green-600">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              Live updates enabled
            </span>
          </div> */}
        </div>
        <div className="flex items-center gap-2">
          {/* Issue Reward Button - only for authorized roles and when feature is enabled */}
          {isFeatureEnabled("issueRewardButton") &&
            (user?.role === "admin" ||
              user?.role === "superadmin" ||
              user?.role === "staff" ||
              user?.role === "support_admin" ||
              user?.role === "support_agent") && (
              <button
                onClick={() => setShowIssueRewardModal(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                title="Issue a reward voucher"
              >
                <GiftIcon className="w-4 h-4" />
                Issue Reward
              </button>
            )}
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            title="Manually refresh data"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card padding="lg">
            <div className="text-sm font-medium text-gray-500">
              Total Vouchers
            </div>
            <div
              className={`mt-2 ${getResponsiveFontSize(analytics.totalVouchers.toString())} font-bold text-gray-900 overflow-hidden`}
            >
              {analytics.totalVouchers}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              {analytics.totalRedemptions} redeemed, {analytics.totalUnredeemed}{" "}
              unredeemed
            </div>
          </Card>
          <Card padding="lg">
            <div className="text-sm font-medium text-gray-500">
              Total Issued Value
            </div>
            <div
              className={`mt-2 ${getResponsiveFontSize("$" + analytics.totalIssuedValue.toFixed(2))} font-bold text-blue-600 overflow-hidden break-words`}
            >
              ${analytics.totalIssuedValue.toFixed(2)}
            </div>
          </Card>
          <Card padding="lg">
            <div className="text-sm font-medium text-gray-500">
              Total Redeemed Value
            </div>
            <div
              className={`mt-2 ${getResponsiveFontSize("$" + analytics.totalValueRedeemed.toFixed(2))} font-bold text-green-600 overflow-hidden break-words`}
            >
              ${analytics.totalValueRedeemed.toFixed(2)}
            </div>
          </Card>
          <Card padding="lg">
            <div className="text-sm font-medium text-gray-500">
              Total Unredeemed Value
            </div>
            <div
              className={`mt-2 ${getResponsiveFontSize("$" + analytics.totalUnredeemedValue.toFixed(2))} font-bold text-yellow-600 overflow-hidden break-words`}
            >
              ${analytics.totalUnredeemedValue.toFixed(2)}
            </div>
          </Card>
          <Card padding="lg">
            <div className="text-sm font-medium text-gray-500">This Month</div>
            <div
              className={`mt-2 ${getResponsiveFontSize(analytics.totalThisMonth.toString())} font-bold text-primary-600 overflow-hidden`}
            >
              {analytics.totalThisMonth}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              ${analytics.valueThisMonth.toFixed(2)} redeemed
            </div>
          </Card>
        </div>
      )}

      {/* Chart */}
      <LineChart
        data={chartData}
        externalDateFilter={
          dateFrom || dateTo ? { from: dateFrom, to: dateTo } : undefined
        }
      />

      {/* Filters */}
      <Card padding="md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {/* Search: code, email, or the issuing / redeeming business (migration 2026-10-06-370) */}
          <RedemptionsReportSearchField
            value={searchTerm}
            onChange={setSearchTerm}
          />

          {/* Date basis — what "from"/"to" below actually measure. Explicit, because the
              old "Date From"/"Date To" labels never said, and they only ever meant issued. */}
          <div>
            <label
              htmlFor="redemptions-date-basis"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Dates refer to
            </label>
            <select
              id="redemptions-date-basis"
              value={dateBasis}
              onChange={(e) =>
                setDateBasis(e.target.value as RedemptionDateBasis)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="issued">
                {REDEMPTION_DATE_BASIS_LABELS.issued}
              </option>
              <option value="redeemed">
                {REDEMPTION_DATE_BASIS_LABELS.redeemed}
              </option>
            </select>
          </div>

          {/* Date From — labelled with the active basis so the meaning is readable
              without opening the select. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {REDEMPTION_DATE_BASIS_LABELS[dateBasis]} from
            </label>
            <input
              type="date"
              aria-label={`${REDEMPTION_DATE_BASIS_LABELS[dateBasis]} from`}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {REDEMPTION_DATE_BASIS_LABELS[dateBasis]} to
            </label>
            <input
              type="date"
              aria-label={`${REDEMPTION_DATE_BASIS_LABELS[dateBasis]} to`}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Organization / Promoter - Only show for non-partner users.
              Value is the org UUID (label = name) so an org that only PROMOTES events
              (and carries no facility of its own) is selectable, and duplicate names are
              distinguishable. */}
          {user?.role !== "partner" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organization / Promoter
              </label>
              <select
                value={selectedOrganization}
                onChange={(e) => setSelectedOrganization(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Organizations / Promoters</option>
                {organizationOptions.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Location - Only show if organization is selected and has locations */}
          {user?.role !== "partner" &&
            selectedOrganization &&
            availableLocations.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Locations</option>
                  {availableLocations.map((loc: string) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
              </div>
            )}

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(
                  e.target.value as
                    | "all"
                    | "redeemed"
                    | "unredeemed"
                    | "expired"
                    | "test"
                    | "cancelled",
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Statuses</option>
              <option value="redeemed">Redeemed</option>
              <option value="unredeemed">Unredeemed</option>
              <option value="expired">Expired</option>
              <option value="test">Test</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Voucher Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Voucher Category
            </label>
            <select
              value={voucherTypeFilter}
              onChange={(e) => setVoucherTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Categories</option>
              <option value="event">Event</option>
              <option value="general">General</option>
              <option value="retail">Retail</option>
            </select>
          </div>

          {/* Clear Filters and Export Buttons */}
          <div className="flex flex-col justify-end gap-2">
            <button
              onClick={() => {
                setSearchTerm("");
                setDateFrom("");
                setDateTo("");
                setSelectedOrganization("");
                setSelectedLocation("");
                setStatusFilter("all");
                setVoucherTypeFilter("");
                setEventIdFilter("");
              }}
              className="w-full px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors text-sm"
            >
              Clear Filters
            </button>
            <button
              onClick={handleExport}
              disabled={totalCount === 0 || isExporting}
              className="w-full px-3 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting
                ? exportProgress
                  ? `Exporting… ${exportProgress.done.toLocaleString()} of ${exportProgress.total.toLocaleString()}`
                  : "Exporting..."
                : `Export All (${totalCount})`}
            </button>
          </div>
        </div>

        {/* Export refusal / failure. Full width beneath the filter grid so a long message
            (the row-ceiling text names the actual count and the limit) stays readable. */}
        {exportError && (
          <div
            role="alert"
            className="mt-4 flex items-start gap-3 rounded border border-amber-300 bg-amber-50 px-4 py-3"
          >
            <span
              aria-hidden="true"
              className="text-amber-600 font-bold leading-5"
            >
              !
            </span>
            <div className="flex-1 text-sm text-amber-900">
              <p className="font-semibold">Export not completed</p>
              <p className="mt-0.5">{exportError}</p>
            </div>
            <button
              onClick={() => setExportError(null)}
              aria-label="Dismiss export error"
              className="text-amber-700 hover:text-amber-900 text-sm font-medium"
            >
              Dismiss
            </button>
          </div>
        )}
        {/* Non-blocking: a name lookup failed, so some rows show blank business/location/event names. Never
            silent (2026-10-06): the export refuses outright in this case; the list keeps the rows it has. */}
        {nameLookupFailures.length > 0 && (
          <div
            role="status"
            data-testid="name-lookup-notice"
            className="mt-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            Some business/contact names couldn&apos;t be loaded, so they show as
            blank below. Refresh the page to try again.
          </div>
        )}
      </Card>

      {/* Mobile Card Layout (< md) */}
      <div className="md:hidden space-y-3">
        {redemptions.length === 0 ? (
          <Card padding="lg">
            <p className="text-center text-gray-500">
              {isFetching ? "Loading..." : "No vouchers found"}
            </p>
          </Card>
        ) : (
          redemptions.map((redemption) => {
            const statusVariant =
              redemption.status === "test"
                ? "info"
                : redemption.is_cancelled || redemption.status === "cancelled"
                  ? "error"
                  : redemption.is_redeemed
                    ? "success"
                    : redemption.end_date && redemption.end_date < Date.now()
                      ? "error"
                      : "warning";
            const statusText =
              redemption.status === "test"
                ? "Test"
                : redemption.is_cancelled || redemption.status === "cancelled"
                  ? "Cancelled"
                  : redemption.is_redeemed
                    ? "Redeemed"
                    : redemption.end_date && redemption.end_date < Date.now()
                      ? "Expired"
                      : "Unredeemed";

            return (
              <Card key={redemption.id} padding="md">
                {/* Card Header: Code + Status */}
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono font-bold text-primary-600 text-sm">
                    {redemption.voucher_code}
                  </span>
                  <Badge variant={statusVariant} size="sm">
                    {statusText}
                  </Badge>
                </div>

                {/* Card Body: Key fields in 2-column grid */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div>
                    <span className="text-gray-500 text-xs block">
                      Category
                    </span>
                    <Badge
                      variant={
                        redemption.voucherCategory === "event"
                          ? "secondary"
                          : redemption.voucherCategory === "general"
                            ? "success"
                            : "gray"
                      }
                      size="sm"
                    >
                      {redemption.voucherCategory === "event"
                        ? "Event"
                        : redemption.voucherCategory === "general"
                          ? "General"
                          : "Retail"}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs block">Value</span>
                    <span className="font-semibold text-gray-900">
                      $
                      {(
                        redemption.applied_amount || redemption.discount_value
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs block">Email</span>
                    <span
                      className="text-gray-900 truncate block cursor-pointer"
                      onClick={() => handleCopyEmail(redemption.email)}
                      title={redemption.email}
                    >
                      {redemption.email}
                    </span>
                    {copiedEmail === redemption.email && (
                      <span className="text-green-600 text-xs">Copied!</span>
                    )}
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs block">
                      {redemption.is_redeemed ? "Redeemed" : "Created"}
                    </span>
                    <span className="text-gray-900">
                      {redemption.redeemed_at
                        ? new Date(redemption.redeemed_at).toLocaleDateString()
                        : redemption.created_at
                          ? new Date(redemption.created_at).toLocaleDateString()
                          : "-"}
                    </span>
                  </div>
                  {(redemption.vendor_name ||
                    redemption.booth_id ||
                    redemption.redeemed_by) && (
                    <div className="col-span-2">
                      <span className="text-gray-500 text-xs block">
                        Business Name
                      </span>
                      <span className="text-gray-900">
                        {redemption.vendor_name ||
                          redemption.booth_id ||
                          redemption.redeemed_by}
                      </span>
                    </div>
                  )}
                  {(redemption.locationName || redemption.organizationName) && (
                    <div className="col-span-2">
                      <span className="text-gray-500 text-xs block">
                        Organization
                      </span>
                      <span
                        className="text-gray-900 truncate block"
                        title={
                          redemption.locationName || redemption.organizationName
                        }
                      >
                        {redemption.locationName || redemption.organizationName}
                      </span>
                    </div>
                  )}
                  {redemption.voucherSource &&
                    redemption.voucherCategory !== "retail" && (
                      <div className="col-span-2">
                        <span className="text-gray-500 text-xs block">
                          Source
                        </span>
                        <span
                          className="text-gray-900 truncate block"
                          title={redemption.voucherSource}
                        >
                          {redemption.voucherSource}
                        </span>
                      </div>
                    )}
                </div>

                {/* Vendor Paid toggle on redeemed vouchers */}
                {redemption.is_redeemed && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                    <div>
                      <span className="text-gray-500 text-xs block">
                        Vendor Paid
                      </span>
                      <span
                        className={`text-sm font-medium ${
                          redemption.vendor_paid
                            ? "text-green-700"
                            : "text-gray-500"
                        }`}
                      >
                        {redemption.vendor_paid ? "Paid" : "Unpaid"}
                      </span>
                    </div>
                    {canToggleVendorPaid && (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!!redemption.vendor_paid}
                        aria-label={
                          redemption.vendor_paid
                            ? "Unmark vendor paid"
                            : "Mark vendor paid"
                        }
                        onClick={() =>
                          handleToggleVendorPaid(
                            redemption.id,
                            !redemption.vendor_paid,
                          )
                        }
                        disabled={vendorPaidPending === redemption.id}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                          redemption.vendor_paid
                            ? "bg-green-600"
                            : "bg-gray-300"
                        }`}
                      >
                        <span
                          aria-hidden="true"
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            redemption.vendor_paid
                              ? "translate-x-5"
                              : "translate-x-0"
                          }`}
                        />
                      </button>
                    )}
                  </div>
                )}

                {/* Actions for admin roles */}
                {(user?.role === "finance" ||
                  user?.role === "admin" ||
                  user?.role === "superadmin") &&
                  !redemption.is_redeemed &&
                  !redemption.is_cancelled &&
                  redemption.status !== "test" && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                      <button
                        onClick={() =>
                          handleCancelVoucher(
                            redemption.id,
                            redemption.voucher_code,
                          )
                        }
                        disabled={cancellingVoucherId === redemption.id}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 rounded hover:bg-red-100 transition-colors text-sm disabled:opacity-50"
                      >
                        <XCircleIcon className="w-4 h-4" />
                        {cancellingVoucherId === redemption.id
                          ? "Cancelling..."
                          : "Cancel"}
                      </button>
                      <button
                        onClick={() =>
                          handleTestVoucher(
                            redemption.id,
                            redemption.voucher_code,
                          )
                        }
                        disabled={cancellingVoucherId === redemption.id}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors text-sm disabled:opacity-50"
                      >
                        {cancellingVoucherId === redemption.id
                          ? "Processing..."
                          : "Test"}
                      </button>
                    </div>
                  )}
              </Card>
            );
          })
        )}
      </div>

      {/* Desktop Table (>= md) */}
      <Card padding="none" className="overflow-hidden hidden md:block">
        {/* Top horizontal scroll bar - with visible border to indicate scrollability */}
        <div
          ref={topScrollRef}
          onScroll={handleTopScroll}
          className="overflow-x-scroll overflow-y-hidden scrollbar-visible"
          style={{
            height: "20px",
            scrollbarWidth: "thin",
            scrollbarColor: "#CBD5E0 #F7FAFC",
            backgroundColor: "#F7FAFC",
            borderTop: "1px solid #E2E8F0",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          {/* Dummy div to match table width */}
          <div style={{ width: "1600px", height: "1px" }}></div>
        </div>

        {/* Table with bottom scroll bar */}
        <div
          ref={bottomScrollRef}
          onScroll={handleBottomScroll}
          className="overflow-x-scroll scrollbar-visible"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#CBD5E0 #F7FAFC",
          }}
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Voucher Source
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Business Name
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    if (sortField === "date") {
                      setSortDirection(
                        sortDirection === "asc" ? "desc" : "asc",
                      );
                    } else {
                      setSortField("date");
                      setSortDirection("desc");
                    }
                  }}
                >
                  <div className="flex items-center gap-1">
                    Date
                    {sortField === "date" && (
                      <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    if (sortField === "value") {
                      setSortDirection(
                        sortDirection === "asc" ? "desc" : "asc",
                      );
                    } else {
                      setSortField("value");
                      setSortDirection("desc");
                    }
                  }}
                >
                  <div className="flex items-center gap-1">
                    Value
                    {sortField === "value" && (
                      <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    if (sortField === "status") {
                      setSortDirection(
                        sortDirection === "asc" ? "desc" : "asc",
                      );
                    } else {
                      setSortField("status");
                      setSortDirection("desc");
                    }
                  }}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortField === "status" && (
                      <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                    )}
                  </div>
                </th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[180px]">
                  Email
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    if (sortField === "organization") {
                      setSortDirection(
                        sortDirection === "asc" ? "desc" : "asc",
                      );
                    } else {
                      setSortField("organization");
                      setSortDirection("asc");
                    }
                  }}
                >
                  <div className="flex items-center gap-1">
                    Organization/Location
                    {sortField === "organization" && (
                      <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                    )}
                  </div>
                </th>
                {/* Redemption Location column - only for partners */}
                {user?.role === "partner" && (
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Redemption Location
                  </th>
                )}
                {/* Event column - hidden for partners. Shows the EFFECTIVE event (see the
                    cell) by name, so an event-tied voucher is distinguishable at a glance
                    from an ordinary retail one. */}
                {user?.role !== "partner" && (
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event
                  </th>
                )}
                {/* Vendor Paid column — visible to everyone who can see redemptions */}
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendor Paid
                </th>
                {/* Actions column heading placeholder - Vendor Name removed */}
                {/* Actions column - only for finance/admin/superadmin */}
                {(user?.role === "finance" ||
                  user?.role === "admin" ||
                  user?.role === "superadmin") && (
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {redemptions.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      // Base columns: 8, +1 for Redemption Location (partners), +1 for Event ID (non-partners), +1 for Vendor Paid, +1 for Actions (admin roles)
                      9 +
                      (user?.role === "partner" ? 1 : 0) +
                      (user?.role !== "partner" ? 1 : 0) +
                      (user?.role === "finance" ||
                      user?.role === "admin" ||
                      user?.role === "superadmin"
                        ? 1
                        : 0)
                    }
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    {isFetching ? "Loading..." : "No vouchers found"}
                  </td>
                </tr>
              ) : (
                redemptions.map((redemption) => (
                  <tr key={redemption.id} className="hover:bg-gray-50">
                    <td className="px-3 py-4 whitespace-nowrap">
                      <span className="font-mono font-bold text-primary-600">
                        {redemption.voucher_code}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-900 max-w-[200px]">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={
                            redemption.voucherCategory === "event"
                              ? "secondary"
                              : redemption.voucherCategory === "general"
                                ? "success"
                                : "gray"
                          }
                          size="sm"
                        >
                          {redemption.voucherCategory === "event"
                            ? "Event"
                            : redemption.voucherCategory === "general"
                              ? "General"
                              : "Retail"}
                        </Badge>
                        {/* Only show source text for event and general vouchers */}
                        {redemption.voucherCategory !== "retail" &&
                          redemption.voucherSource && (
                            <span
                              className="truncate"
                              title={redemption.voucherSource}
                            >
                              {redemption.voucherSource.length > 25
                                ? `${redemption.voucherSource.substring(0, 25)}...`
                                : redemption.voucherSource}
                            </span>
                          )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {redemption.vendor_name ||
                        redemption.booth_id ||
                        redemption.redeemed_by ||
                        ""}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {redemption.redeemed_at
                        ? new Date(redemption.redeemed_at).toLocaleDateString()
                        : redemption.created_at
                          ? new Date(redemption.created_at).toLocaleDateString()
                          : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      $
                      {(
                        redemption.applied_amount || redemption.discount_value
                      ).toFixed(2)}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <Badge
                        variant={
                          redemption.status === "test"
                            ? "info"
                            : redemption.is_cancelled ||
                                redemption.status === "cancelled"
                              ? "error"
                              : redemption.is_redeemed
                                ? "success"
                                : redemption.end_date &&
                                    redemption.end_date < Date.now()
                                  ? "error"
                                  : "warning"
                        }
                        size="sm"
                      >
                        {redemption.status === "test"
                          ? "Test"
                          : redemption.is_cancelled ||
                              redemption.status === "cancelled"
                            ? "Cancelled"
                            : redemption.is_redeemed
                              ? "Redeemed"
                              : redemption.end_date &&
                                  redemption.end_date < Date.now()
                                ? "Expired"
                                : "Unredeemed"}
                      </Badge>
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 max-w-[180px]">
                      <div className="group relative">
                        <span
                          className="truncate block cursor-pointer"
                          onClick={() => handleCopyEmail(redemption.email)}
                        >
                          {redemption.email}
                        </span>
                        {/* Tooltip on hover */}
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50">
                          <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg max-w-xs">
                            <div className="flex items-center gap-2">
                              <span className="break-all">
                                {redemption.email}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyEmail(redemption.email);
                                }}
                                className="flex-shrink-0 p-1 hover:bg-gray-700 rounded"
                                title="Copy email"
                              >
                                <ClipboardDocumentIcon className="h-4 w-4" />
                              </button>
                            </div>
                            {copiedEmail === redemption.email && (
                              <div className="text-green-400 text-xs mt-1">
                                Copied!
                              </div>
                            )}
                          </div>
                          <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-900">
                      {redemption.locationName || redemption.organizationName}
                    </td>
                    {/* Redemption Location cell - only for partners */}
                    {user?.role === "partner" && (
                      <td className="px-3 py-4 text-sm text-gray-900">
                        {redemption.is_redeemed ? (
                          redemption.redemptionLocationName ||
                          redemption.locationName ||
                          redemption.organizationName
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    )}
                    {/* Event cell - hidden for partners.

                        Reads the EFFECTIVE event, not the raw column. An operator-issued
                        voucher carries its event on the TICKET
                        (redemption_tokens.event_id), not on vouchers.event_id, so reading
                        the raw column left every one of those rows blank — which is
                        exactly what was reported. The RPCs have returned
                        effective_event_id since 2026-08-12; only the client was looking in
                        the wrong place. Measured on PROD: it resolves for 1,734 of 1,734
                        such vouchers.

                        Shows the NAME (2026-09-10-016 adds effective_event_title), falling
                        back to a short id only if a title is somehow missing — a truncated
                        UUID is not something an operator can act on. */}
                    {user?.role !== "partner" &&
                      (() => {
                        const eventId =
                          (
                            redemption as {
                              effective_event_id?: string | null;
                            }
                          ).effective_event_id ?? redemption.event_id;
                        const eventTitle = (
                          redemption as {
                            effective_event_title?: string | null;
                          }
                        ).effective_event_title;
                        return (
                          <td className="px-3 py-4 text-sm">
                            {eventId ? (
                              <button
                                onClick={() => navigate(`/events/${eventId}`)}
                                className="text-primary-600 hover:text-primary-800 hover:underline text-left"
                                title={`View event ${eventTitle ?? eventId}`}
                              >
                                {eventTitle ?? `${eventId.substring(0, 8)}...`}
                              </button>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        );
                      })()}
                    {/* Vendor Name column removed */}
                    {/* Vendor Paid cell — toggle on redeemed only */}
                    <td className="px-3 py-4 whitespace-nowrap text-sm">
                      {redemption.is_redeemed ? (
                        <div className="flex items-center gap-2">
                          {canToggleVendorPaid && (
                            <button
                              type="button"
                              role="switch"
                              aria-checked={!!redemption.vendor_paid}
                              aria-label={
                                redemption.vendor_paid
                                  ? "Unmark vendor paid"
                                  : "Mark vendor paid"
                              }
                              onClick={() =>
                                handleToggleVendorPaid(
                                  redemption.id,
                                  !redemption.vendor_paid,
                                )
                              }
                              disabled={vendorPaidPending === redemption.id}
                              title={
                                redemption.vendor_paid
                                  ? "Click to unmark (clears who/when)"
                                  : "Click to mark paid"
                              }
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                                redemption.vendor_paid
                                  ? "bg-green-600"
                                  : "bg-gray-300"
                              }`}
                            >
                              <span
                                aria-hidden="true"
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  redemption.vendor_paid
                                    ? "translate-x-5"
                                    : "translate-x-0"
                                }`}
                              />
                            </button>
                          )}
                          <span
                            className={`text-xs font-medium ${
                              redemption.vendor_paid
                                ? "text-green-700"
                                : "text-gray-500"
                            }`}
                          >
                            {redemption.vendor_paid ? "Paid" : "Unpaid"}
                          </span>
                        </div>
                      ) : (
                        <span
                          className="text-gray-400"
                          title="Only applicable to redeemed vouchers"
                        >
                          —
                        </span>
                      )}
                    </td>
                    {/* Actions column - only for finance/admin/superadmin */}
                    {(user?.role === "finance" ||
                      user?.role === "admin" ||
                      user?.role === "superadmin") && (
                      <td className="px-3 py-4 whitespace-nowrap text-sm">
                        {!redemption.is_redeemed &&
                        !redemption.is_cancelled &&
                        redemption.status !== "test" ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() =>
                                handleCancelVoucher(
                                  redemption.id,
                                  redemption.voucher_code,
                                )
                              }
                              disabled={cancellingVoucherId === redemption.id}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Cancel this voucher"
                            >
                              <XCircleIcon className="w-4 h-4" />
                              {cancellingVoucherId === redemption.id
                                ? "Cancelling..."
                                : "Cancel"}
                            </button>
                            <button
                              onClick={() =>
                                handleTestVoucher(
                                  redemption.id,
                                  redemption.voucher_code,
                                )
                              }
                              disabled={cancellingVoucherId === redemption.id}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Mark as test voucher (excluded from reports)"
                            >
                              {cancellingVoucherId === redemption.id
                                ? "Processing..."
                                : "Test"}
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">
                            {redemption.is_cancelled
                              ? "Already cancelled"
                              : redemption.status === "test"
                                ? "Test voucher"
                                : "Already redeemed"}
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ConfirmDialog Component */}
      <ConfirmDialogComponent />

      {/* Issue Reward Modal */}
      <IssueRewardModal
        isOpen={showIssueRewardModal}
        onClose={() => setShowIssueRewardModal(false)}
        onSuccess={() => {
          refetch();
        }}
      />

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1 || isFetching}
            className="flex items-center gap-1 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            Previous
          </button>

          <div className="flex items-center gap-2">
            {/* First page */}
            {currentPage > 2 && (
              <>
                <button
                  onClick={() => setCurrentPage(1)}
                  className="px-3 py-1 rounded transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  1
                </button>
                {currentPage > 3 && <span className="text-gray-500">...</span>}
              </>
            )}

            {/* Pages around current */}
            {Array.from({ length: 3 }, (_, i) => currentPage - 1 + i)
              .filter((p) => p >= 1 && p <= totalPages)
              .map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  disabled={isFetching}
                  className={`px-3 py-1 rounded transition-colors ${
                    currentPage === pageNum
                      ? "bg-primary-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {pageNum}
                </button>
              ))}

            {/* Last page */}
            {currentPage < totalPages - 1 && (
              <>
                {currentPage < totalPages - 2 && (
                  <span className="text-gray-500">...</span>
                )}
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  className="px-3 py-1 rounded transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>

          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages, prev + 1))
            }
            disabled={currentPage === totalPages || isFetching}
            className="flex items-center gap-1 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary */}
      <div className="text-sm text-gray-500 text-center">
        {isFetching ? (
          <span className="inline-flex items-center gap-2">
            <span className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full"></span>
            Loading...
          </span>
        ) : (
          <>
            Showing {totalCount > 0 ? startIndex + 1 : 0}-{endIndex} of{" "}
            {totalCount.toLocaleString()} vouchers
            {statusFilter !== "all" && ` (${statusFilter} only)`}
            {debouncedSearchTerm && ` matching "${debouncedSearchTerm}"`}
          </>
        )}
      </div>
    </div>
  );
}
